import { CounterInput } from "@/components/counter-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import { formatPlansForDisplay, PlanDisplay } from "@/lib/plan.utils";
import { SubscriptionStatus } from "@/types/subscription.types";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export default function CancelInitiateDialog() {
  const [showCancelModal, setShowCancelModal] = useState(false);
  return (
    <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
      <DialogTrigger asChild>
        <Button variant="link" className="text-blue-500 underline">
          Cancel Plan
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel Your Plan</DialogTitle>
          <DialogDescription>
            Select the plan you want to cancel and the number of mailboxes. This
            action will redirect you to the cancellation page.
          </DialogDescription>
        </DialogHeader>
        <CancelPlanContent setShowCancelModal={setShowCancelModal} />
      </DialogContent>
    </Dialog>
  );
}

const CancelPlanContent = ({
  setShowCancelModal,
}: {
  setShowCancelModal: (show: boolean) => void;
}) => {
  const [cancelCount, setCancelCount] = useState(1);
  const [selectedPlanToCancel, setSelectedPlanToCancel] =
    useState<PlanDisplay | null>(null);

  const { data: activePlans } = useGetActiveSubscriptionPlans();
  const navigate = useNavigate();

  const formattedPlans = useMemo(
    () => formatPlansForDisplay(activePlans),
    [activePlans]
  );

  // Handler for cancel plan submission
  const handleCancelPlan = () => {
    if (!selectedPlanToCancel) return;

    if (selectedPlanToCancel.pendingRequests.cancellation) {
      toast.error(
        "You have a pending cancellation request. Please wait for it to be processed."
      );
      return;
    }

    if (selectedPlanToCancel.pendingRequests.downgrade) {
      toast.error(
        "You have a pending downgrade request. Please wait for it to be processed."
      );
      return;
    }

    navigate({
      to: "/cancelPlan",
      search: {
        planType: selectedPlanToCancel.type,
        subscriptionId: selectedPlanToCancel.subscriptionId,
        planId: selectedPlanToCancel.planId,
        quantity: cancelCount,
      },
    });
  };

  const isSubmitDisabled =
    !selectedPlanToCancel || cancelCount > selectedPlanToCancel.quantity;

  return (
    <>
      <div className="py-4 space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Select Plan</label>
          <Select
            value={selectedPlanToCancel?.uniqueId}
            onValueChange={(value) => {
              const plan = formattedPlans.find((p) => p.uniqueId === value);

              if (plan) {
                setSelectedPlanToCancel(plan);
                // Reset cancel count when changing plans
                setCancelCount(plan.quantity);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a plan to cancel">
                <span className="text-left">
                  {selectedPlanToCancel
                    ? `${selectedPlanToCancel?.category} - ${selectedPlanToCancel?.name} (${selectedPlanToCancel?.interval})`
                    : "Select a plan to cancel"}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {formattedPlans.map((plan) => (
                <SelectItem
                  key={`${plan.subscriptionId}-${plan.planId}-${plan.interval}`}
                  value={plan.uniqueId}
                  disabled={
                    plan.status === SubscriptionStatus.PENDING ||
                    !!plan.pendingRequests.cancellation ||
                    !!plan.pendingRequests.downgrade
                  }
                  wrapInItemText={false}
                >
                  <div className="w-full pr-8 flex items-center justify-between">
                    <div>
                      <div className="flex items-center justify-between w-full">
                        <span className="font-medium">
                          {plan.category} - {plan.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <span>{plan.interval}</span>
                        <span>•</span>
                        <span>{plan.quantity} mailboxes</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      {plan.pendingRequests.downgrade && (
                        <Badge
                          variant="default"
                          className="text-xs font-normal ml-2 bg-amber-100 text-amber-500 hover:bg-amber-100 hover:text-amber-500 border-none shadow-none"
                        >
                          Pending Downgrade
                        </Badge>
                      )}
                      {plan.pendingRequests.cancellation && (
                        <Badge
                          variant="default"
                          className="text-xs font-normal ml-2 bg-amber-100 text-amber-500 hover:bg-amber-100 hover:text-amber-500 border-none shadow-none"
                        >
                          Pending Cancellation
                        </Badge>
                      )}
                      {plan.status === SubscriptionStatus.PENDING && (
                        <Badge
                          variant="default"
                          className="text-xs font-normal ml-2 bg-red-100 text-red-500 hover:bg-red-100 hover:text-red-500 border-none shadow-none"
                        >
                          Unpaid
                        </Badge>
                      )}
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedPlanToCancel && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Mailboxes to Cancel</label>
            <CounterInput
              title=""
              count={cancelCount}
              setCount={setCancelCount}
              min={1}
              max={selectedPlanToCancel.quantity}
            />
            <p className="text-xs text-muted-foreground mt-1">
              You currently have {selectedPlanToCancel.quantity} mailboxes. You
              cannot cancel more than your current quantity.
            </p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => setShowCancelModal(false)}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleCancelPlan}
          disabled={isSubmitDisabled}
        >
          Continue to Cancellation
        </Button>
      </DialogFooter>
    </>
  );
};
